import { useTranslation } from 'react-i18next'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { LoadingButton } from '@mui/lab'
import { Divider, Grid, Paper, Stack, Typography } from '@mui/material'

import colors from '@/assets/scss/_themes-vars.module.scss'

import TextInputController from '@/components/ui/inputs/TextInputController'

import AccountSelectionTable, { AccountData } from './AccountSelectionTable'
import PolicySelectionTable, { PolicyData } from './PolicySelectionTable'

const groupFormSchema = yup.object({
  accounts: yup.array(yup.string().required()).required(),
  name: yup.string().required(),
  policies: yup.array(yup.string().required()).required(),
})

type GroupFormValues = yup.InferType<typeof groupFormSchema>

export type GroupFormProps = {
  accounts?: AccountData[]
  initialValues?: GroupFormValues
  isSubmitting?: boolean
  onSubmit: (values: GroupFormValues) => void
  policies?: PolicyData[]
  readOnly?: boolean
}

export function GroupForm(props: GroupFormProps) {
  const { accounts = [], initialValues, isSubmitting = false, onSubmit: onSubmitProp, policies = [], readOnly = false } = props
  const { t } = useTranslation()

  const { control, handleSubmit, setValue } = useForm<GroupFormValues>({
    defaultValues: initialValues ?? { accounts: [], name: '', policies: [] },
    resolver: yupResolver(groupFormSchema),
    mode: readOnly ? 'onSubmit' : 'onBlur',
  })

  const onSubmit: SubmitHandler<GroupFormValues> = (values) => {
    onSubmitProp(values)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        <Paper>
          <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="h2" component="div" noWrap sx={{ fontSize: { sm: '20px', lg: '24px' }, lineHeight: '28px', fontWeight: 700 }}>
                {t('form.group.name.title')}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <Grid container rowGap={3} sx={{ p: 3 }}>
            <Grid item xs={12} md={6}>
              <TextInputController control={control} fullWidth label={t('field.name.label')} name="name" readOnly={readOnly} />
            </Grid>
          </Grid>
        </Paper>
        <Paper>
          <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="h2" component="div" noWrap sx={{ fontSize: { sm: '20px', lg: '24px' }, lineHeight: '28px', fontWeight: 700 }}>
                {t('form.group.staffs.title')}
              </Typography>
              <Typography
                variant="subtitle1"
                component="div"
                noWrap
                sx={{ color: colors.gray500, fontSize: { sm: '14px', lg: '16px' }, lineHeight: '22px', fontWeight: 400 }}
              >
                {t('form.group.staffs.subtitle')}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <Controller
            control={control}
            name="accounts"
            render={({ field }) => (
              <AccountSelectionTable
                data={accounts}
                onSelectRow={(rows) => {
                  setValue(
                    'accounts',
                    rows.map((row) => row.id)
                  )
                }}
                selectedRows={accounts.filter((account) => field.value.includes(account.id))}
                selectable={!readOnly}
              />
            )}
          />
        </Paper>
        <Paper>
          <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="h2" component="div" noWrap sx={{ fontSize: { sm: '20px', lg: '24px' }, lineHeight: '28px', fontWeight: 700 }}>
                {t('form.group.policies.title')}
              </Typography>
              <Typography
                variant="subtitle1"
                component="div"
                noWrap
                sx={{ color: colors.gray500, fontSize: { sm: '14px', lg: '16px' }, lineHeight: '22px', fontWeight: 400 }}
              >
                {t('form.group.policies.subtitle')}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <Controller
            control={control}
            name="policies"
            render={({ field }) => (
              <PolicySelectionTable
                data={policies}
                onSelectRow={(rows) => {
                  setValue(
                    'policies',
                    rows.map((row) => row.id)
                  )
                }}
                selectedRows={policies.filter((policy) => field.value.includes(policy.id))}
                selectable={!readOnly}
              />
            )}
          />
        </Paper>
        <Stack direction="row" justifyContent="end">
          {!readOnly && (
            <LoadingButton data-testid="group-form-submit" loading={isSubmitting} type="submit" variant="contained">
              {t('common:button.submit.label')}
            </LoadingButton>
          )}
        </Stack>
      </Stack>
    </form>
  )
}

export default GroupForm
